<template>
    <Head title="Mes articles" />

    <FrontLayout>
        <div class="w-full text-xl text-gray-800 leading-normal mb-6">
            <h1 class="font-bold font-sans break-normal text-gray-900 pt-6 text-3xl md:text-4xl">
                Mes articles
            </h1>
            <p class="text-sm font-normal text-gray-600 flex items-center">
                <svg class="w5 h-5 fill-current mr-2" viewBox="0 0 24 24">
                    <path class="text-red-300" d="M12 10l12 7-12 7-12-7z"></path>
                    <path class="text-red-500" d="M12 5l12 7-12 7-12-7z"></path>
                    <path class="text-red-700" d="M12 0l12 7-12 7L0 7z"></path>
                </svg>
                Tableau récapitulatif des articles achetés avec Boti Cargo Shipping.
            </p>
        </div>
        <div x-data="handleSelect mb-6">
            <Datatables backgroundClass="bg-primaryOrange">
                <template #title>
                    Liste de mes articles
                    <span class="font-medium">({{ items.total }})</span>
                </template>

                <template #headerButtons>
                    <div class="grid grid-flow-col sm:auto-cols-max sm:justify-end gap-2">
                        <button @click="showSearchModal" class="rounded-md py-1 px-2 bg-white text-primaryOrange hover:bg-primaryOrange-300 hover:text-white">
                            <i class="fad fa-search cursor-pointer"></i>
                        </button>
                    </div>
                </template>
                <template #tableHeader>
                    <th>#</th>
                    <th>Référence</th>
                    <th>Nom</th>
                    <th>Prix</th>
                    <th>Total versé</th>
                    <th>Total restant</th>
                    <th>Statut</th>
                    <th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <span class="sr-only">Menu</span>
                    </th>
                </template>
                <template #tableBody>
                    <template v-for="(item, index) in items.data" :key="item.id">
                        <tr>
                            <td class="whitespace-nowrap">{{ index + 1 }}</td>
                            <td class="whitespace-nowrap">
                                <span class="font-bold text-primaryOrange">{{ item.reference }}</span>
                            </td>
                            <td class="whitespace-nowrap">{{ item.short_name }}</td>
                            <td class="whitespace-nowrap">
                                <div class="text-right font-bold"
                                    :class="item.balance > 0 ? 'text-gray-400' : 'text-primaryOrange-500'"
                                >{{ formatAmount(item.price, currency) }}</div>
                            </td>
                            <td class="whitespace-nowrap">
                                <div class="text-right">
                                    {{ formatAmount(item.total_paid, currency) }}
                                </div>
                            </td>
                            <td class="whitespace-nowrap">
                                <div class="text-right">
                                    {{ formatAmount(item.balance, currency) }}
                                </div>
                            </td>
                            <td class="whitespace-nowrap">
                                <div class="inline-flex font-medium rounded-full text-center px-2.5 py-0.5"
                                    :class="'bg-'+item.status_color+'-100 text-'+item.status_color+'-500 border border-'+item.status_color+'-500'"
                                >{{ item.status_text }}</div>
                            </td>
                            <td class="whitespace-nowrap">
                                <div class="flex justify-end">
                                    <i @click.prevent="openDetails(item)" aria-controls="description-01"
                                        class="fad fa-arrow-down text-sm text-sky hover:text-sky-400 transform
                                        cursor-pointer transition-transform duration-200 ease-in-out"
                                        :class="{ 'rotate-180' : selectedItem && selectedItem.id === item.id }"
                                        :title="selectedItem && selectedItem.id === item.id ? 'Cacher' : 'Détails'"
                                    ></i>
                                </div>
                            </td>
                        </tr>
                        <tr id="description-02" role="region" v-show="selectedItem && selectedItem.id === item.id">
                            <td colspan="8">
                                <div class="p-4 bg-gray-100">
                                    <h2 class="bg-gray-200 text-gray-800 font-semibold px-2 py-1 text-left">
                                        Historique des paiements
                                    </h2>
                                    <div class="overflow-x-auto">
                                        <table class="w-full">
                                            <thead class="text-xs font-semibold uppercase text-gray-500 bg-gray-50 border-t border-b border-gray-200">
                                                <tr>
                                                    <th>Référence</th>
                                                    <th>Montant</th>
                                                    <th>Mode de paiement</th>
                                                    <th>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody class="text-xs divide-y divide-gray-200">
                                                <tr v-for="(payment, index) in item.payments" :key="index">
                                                    <td>
                                                        <span class="text-primaryOrange font-semibold ">{{ payment.reference }}</span>
                                                    </td>
                                                    <td>
                                                        <div class="text-right">{{ formatAmount(payment.amount, currency) }}</div>
                                                    </td>
                                                    <td>{{ payment.mode_real_text }}</td>
                                                    <td class="capitalize">{{ formattedDate(payment.created_at) }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </template>
                </template>
            </Datatables>
        </div>

        <Pagination :links="items.links" />

        <JetDialogModal :show="searchModal" @close="closeSearchModal" max-width="xl" backgroundClass="bg-primaryOrange">
            <template #title>Rechercher un article</template>
            <template #content>
                <GlobalFilter @search-launched="closeSearchModal" />
            </template>
        </JetDialogModal>
    </FrontLayout>
</template>

<script>
    import { Head } from '@inertiajs/inertia-vue3';
    import Datatables from '@/Components/Datatables'
    import FrontLayout from '@/Layouts/FrontLayout.vue'
    import CurrencyMixins from '@/Mixins/CurrencyMixins'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import GlobalFilter from '@/Components/GlobalFilter'
    import DateFormatMixins from '@/Mixins/DateFormatMixins'
    import SearchActionMixins from '@/Mixins/SearchActionMixins'

    export default {
        components: {
            Head,
            Datatables,
            FrontLayout,
            GlobalFilter,
            JetDialogModal
        },

        mixins: [CurrencyMixins, SearchActionMixins, DateFormatMixins],

        props: {
            items: Object,
            currency: Object
        },

        data() {
            return {
                selectedItem: null
            }
        },

        methods:{
            openDetails(req) {
                if (this.selectedItem && this.selectedItem.id === req.id) {
                    this.selectedItem = null;
                } else {
                    this.selectedItem = req
                }
            },
        }
    }
</script>
